import React from 'react';
import {
  ArrowRight, Check, Compass, Lightbulb, Target,
  TrendingUp, Users, Zap, Settings, MessageSquare,
  ArrowUpRight, Award, BarChart2, UserPlus, Globe, 
  RefreshCw, Rocket, LayoutGrid, BrainCircuit, PenTool,
  Search, Activity, Eye, ChevronRight
} from 'lucide-react';

// Import the standalone components from main site
import HeaderNav from './HeaderNav';
import Contact from './Contact';
import FooterLan from './FooterLan';

// --- Color Palette ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',  
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb', 
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff'
};

const SPACING = {
  xs: 'px-2 py-1',
  sm: 'px-4 py-2',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-16 md:py-20 lg:py-24',
  sectionMedium: 'py-12 md:py-16 lg:py-20',
  sectionCompact: 'py-8 md:py-12 lg:py-16',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-8'
};

// --- Reusable Button Component ---
const Button = ({
  href,
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  icon: IconComponent,
  iconPosition = 'right',
  fullWidth = false,
  className = '',
  type = 'button',
  target,
  rel
}) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2 font-semibold
    transition-all duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    ${fullWidth ? 'w-full' : ''}
    whitespace-nowrap
  `;

  const sizeStyles = {
    xs: 'px-3 py-1.5 text-xs rounded-md',
    sm: 'px-4 py-2 text-sm rounded-md',
    md: 'px-5 py-2.5 text-base rounded-lg',
    lg: 'px-6 py-3 text-lg rounded-lg'
  };

  const variants = {
    primary: `bg-primary hover:bg-primaryDark text-white focus-visible:ring-primary shadow-md hover:shadow-lg active:shadow-sm`,
    secondary: `bg-secondary hover:bg-secondaryDark text-black focus-visible:ring-secondary shadow-md hover:shadow-lg active:shadow-sm`,
    outlinePrimary: `bg-transparent border-2 border-primary text-primary hover:bg-primaryLight focus-visible:ring-primary`,
    outlineGray: `bg-transparent border border-lightGray text-darkGray hover:border-mediumGray hover:text-black focus-visible:ring-mediumGray`,
    text: `bg-transparent text-primary hover:text-primaryDark hover:underline focus-visible:ring-primary`,
    dark: `bg-darkGray hover:bg-darkGray/80 text-white focus-visible:ring-darkGray shadow-md hover:shadow-lg active:shadow-sm`
  };

  const iconElement = IconComponent ? <IconComponent size={size === 'lg' ? 20 : size === 'sm' ? 16 : 18} strokeWidth={2.5} /> : null;

  const content = (
    <>
      {iconPosition === 'left' && iconElement}
      {children}
      {iconPosition === 'right' && iconElement}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={`${baseStyles} ${sizeStyles[size]} ${variants[variant]} ${className}`}
        target={target}
        rel={rel}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseStyles} ${sizeStyles[size]} ${variants[variant]} ${className}`}
    >
      {content}
    </button>
  );
};

// --- Subpage Navigation (breadcrumbs) Component ---
const SubpageNav = () => {
  return (
    <div className="bg-offWhite py-3 border-b border-lightGray">
      <div className={SPACING.container}>
        <div className="flex items-center text-sm">
          <a href="/" className="text-mediumGray hover:text-primary transition-colors">Home</a>
          <ChevronRight size={14} className="mx-2 text-mediumGray" />
          <a href="/#services" className="text-mediumGray hover:text-primary transition-colors">Services</a>
          <ChevronRight size={14} className="mx-2 text-mediumGray" />
          <span className="text-primary font-medium">Business Design</span>
        </div>
      </div>
    </div>
  );
};

// --- Hero Section for Business Design ---
const BusinessDesignHero = () => {
  return (
    <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 md:pb-20 lg:pb-24 overflow-hidden">
      {/* Dynamic background gradient */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primaryLight via-white to-secondaryLight/30 opacity-70"></div>
      
      {/* Abstract shapes for visual interest */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
      
      <div className={`${SPACING.container} z-10 relative`}>
        <div className="flex flex-col lg:flex-row lg:gap-16 items-center">
          {/* Text Content Area */}
          <div className="lg:w-7/12 text-center lg:text-left">
            <div className="inline-block rounded-full bg-primaryLight px-4 py-1 mb-5">
              <h2 className="text-sm font-semibold text-primary uppercase tracking-wide">
                Business Design
              </h2>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-black leading-tight mb-6 md:mb-8">
              Human-<span className="text-primary relative border-b-4 border-primary pb-1">
                Centered
              </span> Business Innovation
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-darkGray mb-8 md:mb-10 lg:mb-12 max-w-2xl mx-auto lg:mx-0">
              Transform your organization through strategic design that prioritizes user experience and creates sustainable business value.
            </p>

            <p className="text-base text-darkGray mb-8 max-w-2xl mx-auto lg:mx-0">
              Our human-centered approach integrates users and stakeholders into every step of the design process, creating tailored solutions that address genuine challenges. We guide your teams from initial concept through strategic implementation, ensuring innovations are both user-friendly and commercially viable.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button href="#contact" variant="primary" size="lg" icon={ArrowRight} className="w-full sm:w-auto">
                Schedule a Consultation
              </Button>
              <Button href="#services" variant="outlineGray" size="lg" icon={Compass} className="w-full sm:w-auto">
                Explore Our Services
              </Button>
            </div>

            {/* Key Principles section */}
            <div className="mt-10 md:mt-12 lg:mt-16 pt-6 md:pt-8 border-t border-lightGray/60">
               <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center lg:justify-start">
                 <div className="flex items-center justify-center sm:justify-start gap-3">
                   <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                     <Globe className="w-5 h-5 text-primary" />
                   </div>
                   <span className="font-medium text-sm sm:text-base text-black">ISO-Aligned Design Methodologies</span>
                 </div>
                 <div className="flex items-center justify-center sm:justify-start gap-3">
                   <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                     <UserPlus className="w-5 h-5 text-primary" />
                   </div>
                   <span className="font-medium text-sm sm:text-base text-black">Collaborative Co-Creation Process</span>
                 </div>
                 <div className="flex items-center justify-center sm:justify-start gap-3">
                   <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                     <BarChart2 className="w-5 h-5 text-primary" />
                   </div>
                   <span className="font-medium text-sm sm:text-base text-black">Measurable Business Outcomes</span>
                 </div>
               </div>
            </div>
          </div>

          {/* Hero Image Area */}
          <div className="lg:w-5/12 mt-12 sm:mt-16 lg:mt-0 w-full max-w-md mx-auto lg:max-w-none lg:mx-0">
            <div className="relative z-20">
              {/* Main image */}
              <div className="rounded-2xl overflow-hidden shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <img
                  src="/img/bd.png"
                  alt="Strategic business design workshop with collaborative team"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              
              {/* Floating card element */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-4 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center gap-3">
                  <Users size={24} className="text-primary" />
                  <div>
                    <div className="text-xs text-mediumGray font-medium">Collaborative Design</div>
                    <div className="text-lg font-bold text-black">User-Driven Innovation</div>
                  </div>
                </div>
              </div>
              
              {/* Second floating element */}
              <div className="absolute -top-4 -right-4 bg-primary/90 text-white rounded-lg shadow-lg py-2 px-4 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="font-bold">Design Thinking Methodology</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Overview Section ---
const OverviewSection = () => {
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className={SPACING.container}>
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-4">
            Strategic Design <span className="text-primary">With Users</span>, Not Just For Them
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          <div className="bg-offWhite rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 rounded-lg bg-primaryLight flex items-center justify-center mb-5">
              <Search className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-black mb-3">Discover Genuine Business Challenges</h3>
            <p className="text-darkGray">
              Our internationally recognized design methodologies including Design Thinking and Design Sprint help organizations identify the true nature of business challenges through user research, stakeholder interviews, and data analysis.
            </p>
          </div>

          <div className="bg-offWhite rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 rounded-lg bg-primaryLight flex items-center justify-center mb-5">
              <RefreshCw className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-black mb-3">Transform Solution Development Processes</h3>
            <p className="text-darkGray">
              We help organizations redesign and optimize their innovation processes, creating systematic approaches to solution development that integrate user feedback at every stage, from ideation to market implementation.
            </p>
          </div>

          <div className="bg-offWhite rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 rounded-lg bg-primaryLight flex items-center justify-center mb-5">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-black mb-3">Co-Create With End Users & Stakeholders</h3>
            <p className="text-darkGray">
              Our collaborative approach incorporates users throughout the design process through interactive workshops, testing sessions, and feedback loops that ensure solutions address real needs and deliver measurable business value.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Three Steps Section ---
const ThreeStepsSection = () => {
  const steps = [
    {
      title: "Understand",
      subtitle: "the Strategic Challenge",
      description: "We begin by deeply understanding your business objectives and user needs through comprehensive research, stakeholder interviews, and market analysis to identify genuine innovation opportunities.",
      icon: BrainCircuit,
      color: "primary"
    },
    {
      title: "Design",
      subtitle: "Human-Centered Solutions",
      description: "Our collaborative design process transforms insights into tangible solutions through ideation workshops, rapid prototyping, and iterative design that incorporates continuous stakeholder feedback.",
      icon: PenTool,
      color: "secondary"
    },
    {
      title: "Implement",
      subtitle: "& Measure Business Impact",
      description: "We support the implementation of validated solutions with roadmapping, strategic planning, and performance measurement to ensure designs deliver quantifiable business value.",
      icon: RefreshCw,
      color: "primary"
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-primaryLight/30 to-white">
      <div className={SPACING.container}>
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-4">
            Our <span className="text-primary">Strategic Framework</span> for Business Design
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-6">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className="lg:flex-1 bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-lightGray/50"
            >
              <div className="text-center mb-6">
                <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 ${
                  step.color === 'primary' ? 'bg-primaryLight' : 'bg-secondaryLight'
                }`}>
                  {React.createElement(step.icon, { 
                    size: 32, 
                    className: step.color === 'primary' ? 'text-primary' : 'text-secondaryDark' 
                  })}
                </div>
                <h3 className="text-2xl font-bold text-black">{step.title}</h3>
                <div className={`h-1 w-16 mx-auto my-3 rounded-full ${
                  step.color === 'primary' ? 'bg-primary' : 'bg-secondary'
                }`}></div>
                <h4 className="text-lg font-semibold text-darkGray">{step.subtitle}</h4>
              </div>
              
              <p className="text-darkGray text-center">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Services Section ---
const ServicesSection = () => {
  const services = [
    {
      icon: MessageSquare,
      title: "Strategic Design Workshops",
      description: "Facilitated collaborative sessions that align stakeholders, identify opportunities, and generate innovative solutions to complex business challenges."
    },
    {
      icon: LayoutGrid,
      title: "Experience Design",
      description: "Comprehensive service design including user journey mapping, wireframing, and prototyping to optimize interactions across all touchpoints."
    },
    {
      icon: Compass,
      title: "Innovation Roadmapping",
      description: "Strategic planning that translates design insights into actionable implementation plans with clear milestones and resource requirements."
    },
    {
      icon: Globe,
      title: "Digital Transformation Design",
      description: "End-to-end redesign of digital processes and touchpoints that enhance both user experience and operational efficiency."
    },
    {
      icon: Users,
      title: "Design Capability Building",
      description: "Training and coaching programs that develop in-house design thinking capabilities and foster a culture of human-centered innovation."
    },
    {
      icon: Rocket,
      title: "Rapid Prototyping & Testing",
      description: "Creation and validation of solution prototypes through iterative testing with real users to refine concepts before full implementation."
    }
  ];

  return (
    <section id="services" className="py-16 md:py-20 lg:py-24 bg-white">
      <div className={SPACING.container}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-4">
            Comprehensive <span className="text-primary">Business Design</span> Services
          </h2>
          <p className="text-lg text-darkGray">
            Our specialized services help organizations transform their approach to innovation through human-centered design methodologies.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {services.map((service, index) => (
            <div key={index} className="bg-offWhite rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-14 h-14 rounded-lg bg-primaryLight flex items-center justify-center mb-6">
                {React.createElement(service.icon, { size: 28, className: 'text-primary' })}
              </div>
              <h3 className="text-xl font-bold text-black mb-3">{service.title}</h3>
              <p className="text-darkGray">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Service Tiers Section ---
const ServiceTiersSection = () => {
  return (
    <section className="py-16 md:py-20 bg-offWhite">
      <div className={SPACING.container}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-4">
            Design Solutions for <span className="text-primary">Every Business Need</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          {/* First Tier */}
          <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-lightGray/50 transition-all duration-300 hover:shadow-xl">
            <div className="h-64 relative">
              <img 
                src="https://placehold.co/600x400/ce2223/ffffff?text=Project+Design" 
                alt="Strategic project design services" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent flex items-end p-6">
                <h3 className="text-2xl font-bold text-white">Strategic Project Design</h3>
              </div>
            </div>
            <div className="p-8">
              <p className="text-darkGray mb-6">
                Our targeted business design approach optimizes individual innovation initiatives, ensuring each project delivers maximum value while aligning with your organization's strategic vision and user needs.
              </p>
              <Button href="#contact" variant="primary" icon={ArrowRight}>
                Schedule a Consultation
              </Button>
            </div>
          </div>

          {/* Second Tier */}
          <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-lightGray/50 transition-all duration-300 hover:shadow-xl">
            <div className="h-64 relative">
              <img 
                src="https://placehold.co/600x400/fdb52a/ffffff?text=Enterprise+Transformation" 
                alt="Enterprise design transformation services" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent flex items-end p-6">
                <h3 className="text-2xl font-bold text-black">Enterprise Design Transformation</h3>
              </div>
            </div>
            <div className="p-8">
              <p className="text-darkGray mb-6">
                Our comprehensive transformation program redesigns your organization's entire approach to innovation, embedding human-centered design principles across departments and creating sustainable capabilities for continuous innovation.
              </p>
              <Button href="#contact" variant="primary" icon={ArrowRight}>
                Schedule a Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Stats Section (Redesigned without black background) ---
const StatsSection = () => {
  const stats = [
    {
      value: "20%",
      label: "higher customer satisfaction achieved by organizations that implement human-centered design methodologies in their business processes.",
      source: "(Forrester Research)"
    },
    {
      value: "56%",
      label: "greater shareholder returns reported by companies that integrate design principles at the leadership level compared to industry benchmarks.",
      source: "(McKinsey Design Index)"
    },
    {
      value: "219%",
      label: "better performance over ten years demonstrated by design-led companies compared to the S&P 500, highlighting the significant business impact of strategic design.",
      source: "(Design Management Institute)"
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-primaryLight to-secondaryLight/30">
      <div className={SPACING.container}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-darkGray">
            The Business <span className="text-primary">Impact</span> of Strategic Design
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-8 bg-white rounded-xl border border-lightGray shadow-lg">
              <div className="text-4xl sm:text-5xl font-bold text-primary mb-4">{stat.value}</div>
              <p className="text-darkGray mb-2">{stat.label}</p>
              <p className="text-sm text-mediumGray">{stat.source}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- CTA Section ---
const CTASection = () => {
  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-primaryLight/50 via-white to-secondaryLight/30">
      <div className={SPACING.container}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-6">
              Transform Your <span className="text-primary">Business Innovation</span> Process Today
            </h2>
            <p className="text-lg text-darkGray mb-8">
              Partner with Lusidea to implement a human-centered approach to business design that delivers measurable results and creates sustainable competitive advantages.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button href="#whiteboards" variant="primary" size="lg" icon={ArrowRight}>
                Explore Our Design Resources
              </Button>
              <Button href="#contact" variant="outlineGray" size="lg" icon={MessageSquare}>
                Schedule a Consultation
              </Button>
            </div>
          </div>
          
          <div className="relative">
            <div className="relative z-10 rounded-xl overflow-hidden shadow-xl transform rotate-1 hover:rotate-0 transition-transform duration-500">
              <img 
                src="https://placehold.co/600x400/ce2223/ffffff?text=Business+Design+Process"
                alt="Human-centered business design process visualization" 
                className="w-full"
              />
            </div>
            
            {/* Decorative elements */}
            <div className="absolute top-8 -left-8 w-40 h-40 bg-primaryLight/50 rounded-full -z-10"></div>
            <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-primary/30 rounded-lg -z-10 transform rotate-12"></div>
            
            {/* Floating elements */}
            <div className="absolute top-1/4 right-0 transform translate-x-1/4 -rotate-6 bg-white p-4 rounded-lg shadow-lg z-20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                  <Activity className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-bold text-black">Measurable ROI</div>
                  <div className="text-xs text-darkGray">Validated outcomes</div>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-1/4 left-0 transform -translate-x-1/4 rotate-6 bg-white p-4 rounded-lg shadow-lg z-20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-bold text-black">User-Centered</div>
                  <div className="text-xs text-darkGray">Collaborative approach</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Newsletter Section ---
const NewsletterSection = () => {
  return (
    <section className="py-16 md:py-20 bg-offWhite" id="whiteboards">
      <div className={SPACING.container}>
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-lightGray">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4">
                Subscribe to Our <span className="text-primary">Design Insights</span>
              </h2>
              <p className="text-darkGray mb-6">
                Receive our monthly digest of business design trends, case studies, and practical methodologies.
              </p>
              <p className="text-darkGray mb-8">
                Our expert team curates the latest insights and best practices in human-centered design to help you stay at the forefront of business innovation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <input 
                  type="email" 
                  placeholder="Your business email" 
                  className="w-full px-4 py-3 rounded-lg border border-lightGray focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button variant="primary" size="md" icon={ArrowRight} className="whitespace-nowrap">
                  Subscribe
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://placehold.co/400x300/ce2223/ffffff?text=Design+Insights"
                  alt="Business design insights newsletter preview" 
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-6 -left-6 w-20 h-20 bg-primaryLight rounded-lg -z-10 transform -rotate-6"></div>
              <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-secondaryLight rounded-full -z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Main Component for Business Design Subpage ---
const BusinessDesignPage = () => {
  return (
    <div className="font-sans antialiased text-darkGray bg-white overflow-x-hidden">
      <HeaderNav />
      <SubpageNav />
      
      <main className="space-y-8 md:space-y-0">
        <BusinessDesignHero />
        <OverviewSection />
        <ThreeStepsSection />
        <ServicesSection />
        <ServiceTiersSection />
        <StatsSection />
        <CTASection />
        <NewsletterSection />
        <Contact />
      </main>
      
      <FooterLan />
    </div>
  );
};

export default BusinessDesignPage;